import {
  ActionIcon, Alert, Anchor, Badge, Button, Container, Fieldset, Grid, Group, LoadingOverlay,
  Menu, MultiSelect, Paper, Select, Stack, Table, Tabs, Text, TextInput, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle, IconAlertTriangle, IconArrowBackUp, IconBuildingBank, IconChevronDown, IconCircleX, IconClipboardList,
  IconEdit, IconFiles, IconPlus, IconRotate, IconTrash, IconUnlink, IconUsers, IconX,
} from '@tabler/icons-react';
import * as React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDirtyFormGuard, useFormSubmitSaver } from '../hooks/useDirtyFormGuard';
import { usePendingCreates } from '../hooks/usePendingCreates';
import { changedFieldLabels, fieldSuffix, labelizeField } from '../utils/toastFields';
import { diffUpdatePayload } from '../utils/diffPayload';
import { overlapConfirmProps, pendingFieldOverlap } from '../utils/pendingOverlap';
import { StickyDetailHeader } from '../components/StickyHeader';
import { SummaryItem } from '../components/SummaryBox';
import { IncompleteBanner } from '../components/IncompleteBanner';
import { TabMissingCount, useMissingFields } from '../components/MissingFields';
import { DocumentsPanel } from '../components/DocumentsPanel';
import { documentResultParts, useDocumentStaging } from '../hooks/useDocuments';
import { useAlertModal } from '../components/AlertModalProvider';
import { useAgreement } from '../hooks/useAgreements';

import { ApiError } from '../api/client';
import { applyApiErrorToForm, isFieldLevelApiError } from '../utils/applyApiError';
import type { AccountInsurance, AccountInsuranceInput, InsurancePolicyPartyRole } from '../api/types';
// PersonRef removed 2026-06-02 (Task #239) — Parties table reads
// server-resolved person_display_name directly from
// InsurancePolicyPartyOut. Re-added 2026-06-10 (#20) for filed-pending
// phantom rows, whose CR payload only carries person_id (no resolved
// name). Fires one lookup per phantom row — rare, acceptable.
import { AccountRef, PersonRef } from '../components/EntityRef';
import { PendingCRBadge } from '../components/PendingCRBadge';
import { StatusExplained } from '../components/StatusExplained';
import {
  useAccountInsurance, useAccountInsuranceReferences, useAttachAccountToPolicy,
  useCascadeDeleteAccountInsurance, useCreateAccountInsurance, useDeleteAccountInsurance,
  useAttachPolicy, useDetachPolicy, useLapsePolicy, useMaturePolicy, useRedeemPolicy,
  useRestoreAccountInsurance, useUpdateAccountInsurance,
} from '../hooks/useAccountInsurance';
import {
  useInsurancePolicyPartiesList,
  useCreateInsurancePolicyParty,
  useDeleteInsurancePolicyParty,
} from '../hooks/useInsurancePolicyParties';
import { AddPolicyPartyModal } from '../components/AddPolicyPartyModal';
import { useAddPartyModal, useNewPartyPersonResume } from '../components/PartyAddSteps';
import type { InsurancePolicyPartyInput, UUID } from '../api/types';
import { useAccount, useAccounts, useDetachAccountFromPolicy } from '../hooks/useAccounts';
import { AgreementRef } from '../components/EntityRef';
import { useConfirmDelete } from '../hooks/useConfirmDelete';
import { CascadeDeleteModal } from '../components/CascadeDeleteModal';
import { DetachPolicyModal } from '../components/DetachPolicyModal';
import { TerminatePolicyModal } from '../components/TerminatePolicyModal';
import { DisabledTooltipButton } from '../components/DisabledTooltipButton';
import { RowSeverIcon } from '../components/RowSeverIcon';
import { AgreementPicker } from '../components/EntityPicker';
import type { CloseReason, UserCloseReason } from '../api/types';
import { CLOSE_REASON_LABELS, PARTY_ROLE_LABELS } from '../api/types';

// Role-chip palette shared with InsurancePolicyPartiesListPage and the
// AgreementDetailPage UI-join — same role always reads the same colour.
const ROLE_COLOURS: Record<InsurancePolicyPartyRole, string> = {
  POLICY_HOLDER: 'blue',
  LIFE_INSURED: 'teal',
  BENEFICIARY: 'orange',
};

// Task #367 (§3g Phase 1 Step B, 2026-06-09): pending-op discriminated
// unions for Parties + Accounts sections. The page lifts state from
// the sections so the single page-level Save handler can batch
// commits across all relationship operations alongside the Identity
// form fields. Mirrors the pattern from AgreementDetailPage's
// pendingPartyOps + pendingDocOps + pendingRMOps queues.
//
// Phase 1.5 (#375, 2026-06-09): the three §3g violators left over from
// Phase 1 (per-row Account Detach, whole-Policy Detach, Terminate menu)
// are now staged-on-Save. The pendingPolicyAccountOp union gains a
// 'detach' kind for per-row Account detach; a new PendingPolicyLifecycleOp
// union models the singleton (at most one queued) lifecycle change to
// the Policy itself (whole-Policy Detach or Terminate Redeem/Lapse/Mature).
type PendingPolicyPartyOp =
  | { kind: 'create'; tempKey: string; payload: InsurancePolicyPartyInput;
      /** Captured at stage time so the pending row renders the Person's
       *  display name without needing a per-row hook lookup. */
      person_display_name: string | null }
  // #23 (2026-06-10, §3h sweep): per-row staged detach — mirrors
  // Agreement Parties (#327 Phase 1) and AccountDetailPage's Persons
  // tab (same task). tempKey = party_id so the failedKeys/Unstage
  // machinery works unchanged for both kinds.
  | { kind: 'delete'; tempKey: string; party_id: UUID };

type PendingPolicyAccountOp =
  | { kind: 'attach'; tempKey: string; account_id: UUID }
  /** Phase 1.5: stage a per-row Account detach from this Policy.
   *  link_account_id is the existing account_id (currently in account_ids[])
   *  whose `opened_by_policy_id` will be NULLed and status flipped to
   *  Inactive. The same maker-checker route handles RM-filings via the
   *  /accounts/{id}/detach-from-policy endpoint (A1.b). */
  | { kind: 'detach'; tempKey: string; link_account_id: UUID };

/** Phase 1.5: lifecycle-level pending ops on the Policy itself.
 *  Singleton: at most one queued at a time (a Policy has one lifecycle —
 *  you can't simultaneously stage a Detach and a Terminate). Stored as
 *  a single nullable state slot, not an array. */
type PendingPolicyLifecycleOp =
  | { kind: 'detach-whole-policy' }
  | { kind: 'attach-to-agreement'; agreement_id: UUID }
  | { kind: 'terminate'; reason: CloseReason };

let _tempKeySeq = 0;
function _nextTempKey(): string {
  _tempKeySeq += 1;
  return `tmp-${_tempKeySeq}`;
}

// H1.6: account_id (singular) is no longer on the policy. The Identity
// form carries no account information — accounts are managed via the
// Accounts tab after the policy is saved. On NEW, if the user arrived
// from an Account detail page (?account_id=X), we still pass that to the
// API as a one-element account_ids[] so the join row lands under the
// same Compliance approval as the policy CR.
interface FormValues {
  insurer: string; policy_number: string;
  fund_type: string; fund_number: string;
  contact_name: string; contact_phone: string; contact_fax: string; contact_email: string;
}
const EMPTY: FormValues = {
  insurer: '', policy_number: '',
  fund_type: '', fund_number: '',
  contact_name: '', contact_phone: '', contact_fax: '', contact_email: '',
};

export function AccountInsuranceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  // #274 Wave A.2 (2026-06-08): the canonical create path is now
  // ?agreement_id=X (sent by AddInsurancePolicyModal). The legacy
  // ?account_id=X path is preserved for the AccountDetailPage button —
  // we read the picked Account and derive its agreement_id, treating
  // the Account itself as a separate Direct Account (NOT auto-attaching
  // it as Indirect on the new Policy; that would require detach+reattach
  // which is out of scope for create flow).
  const presetAgreementId = isNew ? (searchParams.get('agreement_id') ?? '') : '';
  const presetAccountId = isNew ? (searchParams.get('account_id') ?? '') : '';
  const returnTo = searchParams.get('return_to');

  // #274 Wave A.2: orphan-Account multi-select state. Selected IDs are
  // sent in `account_ids` on create; backend atomically promotes each
  // to Indirect (sets opened_by_policy_id = new_policy.id) under the
  // same Compliance approval.
  const [orphanPicks, setOrphanPicks] = React.useState<string[]>([]);

  // Phase H1.5: tabbed shell. Identity tab holds the policy's own
  // fields (the existing single-form view). Parties tab adds the new
  // insurance_policy_parties listing — hidden when isNew because there's
  // no policy id to scope parties to until the policy is saved.
  // setActiveTab is defined further down once form / editMode / the
  // dirty-form-guard are in scope (we route tab clicks through them so
  // an unsaved Identity-tab edit can't be silently abandoned).
  const urlTab = searchParams.get('tab');
  const defaultTab = 'identity';
  const activeTab = urlTab ?? defaultTab;

  // Task #245 (2026-06-02): include_deleted=true so deleted insurance
  // policies can be viewed (DELETED state + Restore button).
  const { data, isLoading } = useAccountInsurance(isNew ? undefined : id, true);

  // Phase A3.d (2026-06-09): parent-agreement lookup for the header summary
  // box. Conditional — fires only when the policy has an attached Agreement
  // (Active policies always do; orphan-Inactive policies have agreement_id
  // NULL by schema CHECK).
  const parentAgreementQ = useAgreement(data?.agreement_id ?? undefined);

  // #274 Wave A.2 (2026-06-08): when launched from an Account detail
  // page (?account_id=X), look up that Account to derive the new
  // Policy's agreement_id. Only Direct Accounts (with agreement_id set)
  // can host this flow; Indirect/orphan Accounts would yield NULL.
  const presetAccountQ = useAccount(presetAccountId || undefined, false);
  const derivedAgreementId =
    presetAgreementId
    || (presetAccountQ.data?.agreement_id ?? '');

  // Orphan-Account list — only fetched when we have a target Agreement
  // (presetAgreementId set OR derived from a Direct Account). Even
  // though orphans by definition have no agreement_id, the picker is
  // only meaningful in the create-with-agreement flow.
  const orphansQ = useAccounts({
    orphans_only: true,
    limit: 200,
  });
  const orphanOptions = (orphansQ.data?.items ?? []).map((a) => ({
    value: a.id,
    label: `${a.bank_name}${a.internal_num ? ' #' + a.internal_num : ''}${a.iban ? ' (' + a.iban + ')' : ''}`,
  }));
  const create = useCreateAccountInsurance();
  const update = useUpdateAccountInsurance(id ?? '');
  const queryClient = useQueryClient();
  const del = useDeleteAccountInsurance();
  const cascadeDelete = useCascadeDeleteAccountInsurance();
  const refs = useAccountInsuranceReferences(id, false);
  const [cascadeOpen, setCascadeOpen] = React.useState(false);
  const restore = useRestoreAccountInsurance();
  const confirmDelete = useConfirmDelete();

  const initial: FormValues = isNew || !data ? { ...EMPTY } : {
    insurer: data.insurer, policy_number: data.policy_number,
    fund_type: data.fund_type ?? '', fund_number: data.fund_number ?? '',
    contact_name: data.contact_name ?? '', contact_phone: data.contact_phone ?? '',
    contact_fax: data.contact_fax ?? '', contact_email: data.contact_email ?? '',
  };
  const form = useForm<FormValues>({
    initialValues: initial,
    validate: {
      insurer: (v) => (v ? null : 'Required'),
      policy_number: (v) => (v ? null : 'Required'),
    },
  });
  const [editMode, setEditMode] = React.useState(isNew);
  const { showAlert, confirm } = useAlertModal();

  // P18 (2026-10-10): resume the "Create new person" round-trip, as on the
  // Agreement page. PersonDetailPage ferries the new person's id back as
  // ?new_party_person=<id> on the Parties tab; re-enter Edit mode (Add party is
  // Edit-gated) and reopen the Add-party modal on Attach with it pre-selected.
  const [resumePartyPersonId, setResumePartyPersonId] = React.useState<string | null>(null);
  // Waits for `data` so the form is filled before Edit mode turns on.
  useNewPartyPersonResume(!!data, (pid) => {
    setResumePartyPersonId(pid);
    setEditMode(true);
  });

  // Task #367 (§3g Phase 1 Step B, 2026-06-09): page-level pending-op
  // queues for Parties and Accounts sections. Lifted from the section
  // components so the Save handler can batch commits with the Identity
  // field UPDATE in one transaction (see handleSubmit below).
  const [pendingPartyOps, setPendingPartyOps] = React.useState<PendingPolicyPartyOp[]>([]);
  const [pendingAccountOps, setPendingAccountOps] = React.useState<PendingPolicyAccountOp[]>([]);
  // Phase 1.5 (#375): singleton lifecycle op (Detach whole Policy OR
  // Terminate). At most one — a Policy has one lifecycle.
  const [pendingPolicyLifecycleOp, setPendingPolicyLifecycleOp] = React.useState<PendingPolicyLifecycleOp | null>(null);
  const [failedPartyKeys, setFailedPartyKeys] = React.useState<ReadonlySet<string>>(new Set());
  const [failedAccountKeys, setFailedAccountKeys] = React.useState<ReadonlySet<string>>(new Set());
  // Phase 1.5: lifecycle op may also fail at commit time; surface inline.
  const [failedLifecycleReason, setFailedLifecycleReason] = React.useState<string | null>(null);
  // Per-op failure reason — surfaced as a small red caption on the failed
  // pending row so the user can act on the cause (duplicate, cross-agreement
  // trigger, FK violation, etc.) rather than wonder why their op rejected.
  const [failedReasons, setFailedReasons] = React.useState<Map<string, string>>(new Map());

  // Hooks for the per-op commits during Save batch (Step G). The list
  // sections still use their own list-query hooks for read; mutations
  // are owned by the page now so the toast/error aggregation can be
  // unified across ops.
  const createParty = useCreateInsurancePolicyParty();
  // #23 (2026-06-10): per-row staged detach commits via the same
  // maker-checker delete the party detail page uses (soft-delete of the
  // join row; the Person itself is untouched).
  const deleteParty = useDeleteInsurancePolicyParty();
  const attachAccountToPolicy = useAttachAccountToPolicy();
  // Phase 1.5 mutation hooks. Mirror the existing Pattern B modals'
  // wiring — same backend endpoints, just commit moves from modal-click
  // to Save-Changes-batch.
  const detachAccountFromPolicy = useDetachAccountFromPolicy();
  const detachPolicy = useDetachPolicy();
  const attachPolicy = useAttachPolicy();
  const redeemPolicy = useRedeemPolicy();
  const lapsePolicy = useLapsePolicy();
  const maturePolicy = useMaturePolicy();

  // Task #367: dirty signal includes pending ops + form. The
  // dirty-form-guard fires on route-level navigate-away with any pending
  // work, regardless of which tab is active.
  // P23 (2026-10-10): documents on this policy — staged in Edit mode, sent on Save.
  const docs = useDocumentStaging('insurance_policies', isNew ? undefined : id);
  const hasPendingWork =
    form.isDirty() || pendingPartyOps.length > 0 || pendingAccountOps.length > 0
    || pendingPolicyLifecycleOp !== null || !docs.queue.isEmpty;
  // Pedro 2026-10-10: leaving with unsaved edits offers "Save & leave" (runs this page's Save).
  const saveForGuard = useFormSubmitSaver('account-insurance-form', hasPendingWork, !isNew);
  const confirmDiscard = useDirtyFormGuard(hasPendingWork, saveForGuard);

  // §3g Phase 1 simplification: edit mode persists across tab switches.
  // The dirty-form-guard handles route-level navigate-away; tab switches
  // are URL-search-param changes only and don't discard transaction state.
  // Previous behaviour auto-exited editMode and reset form when leaving
  // Identity tab — that broke the page-level transaction model.
  const setActiveTab = (v: string | null) => {
    if (!v) return;
    const next = new URLSearchParams(searchParams);
    if (v === defaultTab) next.delete('tab');
    else next.set('tab', v);
    setSearchParams(next, { replace: true });
  };
  // Pedro 2026-10-10 (§3h sweep): the policy's completeness rules now show here like on
  // the other record pages — the banner, a violet count per tab and outlined empty inputs.
  const missing = useMissingFields('INSURANCE_POLICY', isNew ? undefined : data?.id);
  const hl = (f: keyof FormValues) => missing.input(f, form.values[f]);

  // Stage handlers passed down to the section components.
  const stagePartyCreate = React.useCallback(
    (payload: InsurancePolicyPartyInput, personDisplayName: string | null) => {
      setPendingPartyOps((prev) => [
        ...prev,
        { kind: 'create', tempKey: _nextTempKey(), payload, person_display_name: personDisplayName },
      ]);
    },
  []);
  // #23 (2026-06-10): stage a per-row party detach. Same-key replace —
  // staging twice is a no-op (tempKey = party_id).
  const stagePartyDelete = React.useCallback((partyId: UUID) => {
    setPendingPartyOps((prev) => [
      ...prev.filter((op) => op.tempKey !== partyId),
      { kind: 'delete', tempKey: partyId, party_id: partyId },
    ]);
  }, []);
  const unstagePartyOp = React.useCallback((tempKey: string) => {
    setPendingPartyOps((prev) => prev.filter((op) => op.tempKey !== tempKey));
    setFailedPartyKeys((prev) => {
      const n = new Set(prev); n.delete(tempKey); return n;
    });
  }, []);
  const stageAccountAttach = React.useCallback((accountId: UUID) => {
    setPendingAccountOps((prev) => [
      ...prev,
      { kind: 'attach', tempKey: _nextTempKey(), account_id: accountId },
    ]);
  }, []);
  // Phase 1.5: stage a per-row Account detach (Indirect → orphan) for an
  // existing alive link. The committed row stays visible in the table
  // until Save commits the detach; the pending detach is rendered as
  // an orange overlay/badge on the row (see PolicyAccountsSection).
  const stageAccountDetach = React.useCallback((linkAccountId: UUID) => {
    setPendingAccountOps((prev) => [
      ...prev,
      { kind: 'detach', tempKey: _nextTempKey(), link_account_id: linkAccountId },
    ]);
  }, []);
  const unstageAccountOp = React.useCallback((tempKey: string) => {
    setPendingAccountOps((prev) => prev.filter((op) => op.tempKey !== tempKey));
    setFailedAccountKeys((prev) => {
      const n = new Set(prev); n.delete(tempKey); return n;
    });
  }, []);

  // Phase 1.5 (#375): lifecycle op stage handlers. Singleton — staging
  // a new one replaces any prior one (the modal's "Stage" button is the
  // user's explicit gesture, so overwriting is fine; if they wanted the
  // prior op they would have Saved or Discarded). Failure state cleared
  // alongside the new staging.
  const stagePolicyLifecycleOp = React.useCallback((op: PendingPolicyLifecycleOp) => {
    setPendingPolicyLifecycleOp(op);
    setFailedLifecycleReason(null);
  }, []);
  const unstagePolicyLifecycleOp = React.useCallback(() => {
    setPendingPolicyLifecycleOp(null);
    setFailedLifecycleReason(null);
  }, []);
  // #31 (2026-06-11): hydration effect replaces the old render-time
  // sentinel guard (`form.values.insurer === ''` → setValues), which
  // snapped the form back whenever the user CLEARED the insurer field.
  // Hydrate when server data arrives or refetches, but never while an
  // edit is open. Pattern shared with the other detail pages.
  React.useEffect(() => {
    if (!isNew && data && !editMode) {
      form.setValues(initial);
      form.resetDirty(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const _err = isNew ? create.error : update.error;
  const apiError = (_err instanceof ApiError && !isFieldLevelApiError(_err))
    ? _err.detail : null;
  const isDeleted = !!data?.deleted_at;

  function handleSubmit(values: FormValues) {
    const payload: AccountInsuranceInput = {
      insurer: values.insurer, policy_number: values.policy_number,
    };
    if (isNew) {
      // #274 Wave A.2 (2026-06-08): set agreement_id (REQUIRED by the
      // backend AccountInsuranceCreate schema since migration 0028).
      // Source order: explicit presetAgreementId (chooser modal path) >
      // derived from preset Account (Account-page button path).
      if (!derivedAgreementId) {
        // Defensive: form validation should have blocked submit but
        // surface a helpful message instead of a 422 from the backend.
        showAlert({
          color: 'red',
          title: 'Missing Agreement',
          message:
            'A new Policy must belong to an Agreement. Open this form from '
            + 'an Agreement detail page (+ Add Insurance Policy) or from a '
            + 'Direct Account detail page.',
        });
        return;
      }
      payload.agreement_id = derivedAgreementId;
      // Indirect Accounts: only the orphan picks. The presetAccountId
      // (if any) is intentionally NOT included — that Account stays
      // Direct on its existing Agreement. Promoting a Direct Account
      // to Indirect requires a separate detach + reattach flow.
      payload.account_ids = orphanPicks;
    }
    const optional: (keyof FormValues)[] = ['fund_type', 'fund_number',
      'contact_name', 'contact_phone', 'contact_fax', 'contact_email'];
    for (const k of optional) {
      const v = values[k];
      if (typeof v === 'string' && v) (payload as Record<string, unknown>)[k] = v;
    }

    if (isNew) {
      // Create flow: no pending Parties/Accounts ops possible here
      // (isNew implies the policy doesn't exist yet, so the Parties /
      // Accounts tabs are disabled). Single mutation, no batch.
      create.mutate(payload, {
        onSuccess: (r) => {
          form.resetDirty();
          // Bug fix (2026-06-09 during A1.f): after create-then-navigate
          // (replace:true), the component re-renders with isNew=false
          // but useState(isNew) doesn't re-initialize, so editMode stays
          // true and the user sees Cancel/Save/Delete instead of Edit.
          // Flip editMode explicitly.
          setEditMode(false);
          if (r.pending) {
            showAlert({
              color: 'orange',
              title: 'Filed for compliance approval',
              message: 'Your new insurance policy was filed. It is not yet visible — Compliance must approve it first.',
            });
            navigate(`/change-requests/${r.changeRequestId}`);
          } else {
            notifications.show({ message: 'Insurance policy created', color: 'green' });
            navigate(`/account-insurance/${r.entity.id}`, { replace: true });
          }
        },
        onError: (err) => { applyApiErrorToForm(form, err); },
      });
      return;
    }

    // Task #372 (§3g Phase 1 Step G, 2026-06-09): page-level Save
    // batches the Identity field UPDATE (if dirty) with every pending
    // Party CREATE and Account ATTACH op. Each op flows through its
    // existing maker-checker path (RM filings queue CRs; Compliance/
    // Admin auto-apply). Aggregate counts feed a single toast.
    void runBatchSave(values);
  }

  async function runBatchSave(values: FormValues) {
    // Per-category counters so the partial-save toast can point the user
    // at the right tab. "applied" = committed immediately (Compliance /
    // Admin); "pending" = filed as a CR (RM); "failed" = rejected.
    let identityApplied = 0, identityPending = 0;
    let partyApplied = 0, partyPending = 0, partyFailed = 0;
    let accountAttachApplied = 0, accountAttachPending = 0;
    let accountDetachApplied = 0, accountDetachPending = 0;
    let accountFailed = 0;
    // Phase 1.5: lifecycle op counters. At most one op so counts are 0 or 1.
    let lifecycleApplied = 0, lifecyclePending = 0, lifecycleFailed = 0;
    let lifecycleLabel: string | null = null;
    const nextFailedPartyKeys = new Set<string>();
    const nextFailedAccountKeys = new Set<string>();
    const nextFailedReasons = new Map<string, string>();
    let nextFailedLifecycleReason: string | null = null;

    // #22 (2026-06-10): Save toast names the changed Identity fields,
    // computed against the pre-save baseline (capped inside fieldSuffix).
    // All FormValues keys humanize cleanly — no overrides needed.
    const changedLabels = changedFieldLabels(values, initial);

    // 1. Identity field UPDATE — only if form is dirty.
    if (form.isDirty()) {
      // #36 (2026-06-11): UPDATE sends ONLY changed fields — accurate
      // op labels, clean CR diffs for the reviewer, and clears persist
      // (changed-to-empty optionals send explicit null; C-120 family).
      const payload = diffUpdatePayload(
        values as unknown as Record<string, unknown>,
        initial as unknown as Record<string, unknown>,
        {
          nullableOnClear: new Set(['fund_type', 'fund_number',
            'contact_name', 'contact_phone', 'contact_fax', 'contact_email']),
        },
      ) as AccountInsuranceInput;
      // #37/old-#347 (2026-06-11, Pedro's decision: warn, don't block):
      // if a pending CR already changes any field in this payload, ask
      // before filing a competing change. Cancel aborts the whole batch
      // (nothing has committed yet) so the user can review first.
      const overlap = pendingFieldOverlap(
        payload as Record<string, unknown>, data?.pending_change_requests ?? null,
      );
      if (overlap) {
        const ok = await confirm(overlapConfirmProps(overlap, labelizeField));
        if (!ok) return;
      }
      try {
        const r = await update.mutateAsync(payload);
        if (r.pending) identityPending += 1; else identityApplied += 1;
      } catch (err) {
        applyApiErrorToForm(form, err);
        // Bail: form-level error means the user must fix Identity
        // before pending ops are attempted. Pending ops stay in queue.
        showAlert({
          color: 'red',
          title: 'Save failed on Identity tab',
          message: err instanceof ApiError ? err.detail : 'Unexpected error.',
        });
        return;
      }
    }

    // 2. Pending party CREATE ops, sequential to avoid race conditions
    //    on overlapping CHECK constraints (e.g. percentage_share sums).
    for (const op of pendingPartyOps) {
      try {
        // #23: branch per op kind — creates and detaches both flow
        // through maker-checker (RM filings produce pending CRs).
        const r = op.kind === 'create'
          ? await createParty.mutateAsync(op.payload)
          : await deleteParty.mutateAsync(op.party_id);
        if (r.pending) partyPending += 1; else partyApplied += 1;
      } catch (err) {
        nextFailedPartyKeys.add(op.tempKey);
        nextFailedReasons.set(op.tempKey,
          err instanceof ApiError ? err.detail : 'Unexpected error.');
        partyFailed += 1;
      }
    }

    // 3. Pending account ATTACH and DETACH ops.
    //    Phase 1.5 (#375): the union now carries both 'attach' (orphan
    //    → Indirect) and 'detach' (Indirect → orphan) kinds. Failure
    //    state is keyed on op.tempKey for both kinds; the failedKeys Set
    //    is global to the account-ops table regardless of kind.
    for (const op of pendingAccountOps) {
      try {
        if (op.kind === 'attach') {
          const r = await attachAccountToPolicy.mutateAsync({
            policy_id: id!,
            account_id: op.account_id,
          });
          if (r.pending) accountAttachPending += 1; else accountAttachApplied += 1;
        } else {
          // op.kind === 'detach'
          const r = await detachAccountFromPolicy.mutateAsync(op.link_account_id);
          if (r.pending) accountDetachPending += 1; else accountDetachApplied += 1;
        }
      } catch (err) {
        nextFailedAccountKeys.add(op.tempKey);
        nextFailedReasons.set(op.tempKey,
          err instanceof ApiError ? err.detail : 'Unexpected error.');
        accountFailed += 1;
      }
    }

    // 3b. P23: staged document adds / removes (settled inside commit(); a failed
    //     one stays staged with its reason).
    const docResult = await docs.commit();

    // 4. Pending lifecycle op (Phase 1.5, #375). At most one. Processed
    //    LAST so any per-row attach/detach has already committed before
    //    the cascade-laden lifecycle change applies. If the lifecycle op
    //    is 'terminate', any Active Indirect accounts (including ones we
    //    just attached) cascade to Closed.
    if (pendingPolicyLifecycleOp) {
      try {
        if (pendingPolicyLifecycleOp.kind === 'detach-whole-policy') {
          // #26 (2026-06-10): detach went maker-checker — RM filings come
          // back pending with a `_detach_plan` footprint CR.
          const r = await detachPolicy.mutateAsync(id!);
          if (r.pending) {
            lifecyclePending += 1;
            lifecycleLabel = 'Detach filed for approval';
          } else {
            lifecycleApplied += 1;
            lifecycleLabel = 'Policy detached';
          }
        } else if (pendingPolicyLifecycleOp.kind === 'attach-to-agreement') {
          // gap-2 (2026-06-16, Phase 4b / C-167): re-attach a limbo Policy
          // to an Agreement from the Policy side (mirror of the
          // Agreement-side "+ Add Insurance Policy → Attach"). Maker-checker
          // like detach; backend enforces the Active/Prospect target (Q6.2).
          const r = await attachPolicy.mutateAsync({
            id: id!, agreementId: pendingPolicyLifecycleOp.agreement_id,
          });
          if (r.pending) {
            lifecyclePending += 1;
            lifecycleLabel = 'Attach filed for approval';
          } else {
            lifecycleApplied += 1;
            lifecycleLabel = 'Policy attached';
          }
        } else {
          // terminate — #26: maker-checker too (`_terminate_plan`).
          const reason = pendingPolicyLifecycleOp.reason;
          const hook = reason === 'REDEEMED' ? redeemPolicy
                     : reason === 'LAPSED'   ? lapsePolicy
                     :                          maturePolicy;
          const r = await hook.mutateAsync(id!);
          const reasonLabel = reason === 'REDEEMED' ? 'Redeemed'
                            : reason === 'LAPSED'   ? 'Lapsed'
                            :                          'Matured';
          if (r.pending) {
            lifecyclePending += 1;
            lifecycleLabel = `Terminate (${reasonLabel}) filed for approval`;
          } else {
            lifecycleApplied += 1;
            lifecycleLabel = `Policy ${reasonLabel}`;
          }
        }
      } catch (err) {
        nextFailedLifecycleReason =
          err instanceof ApiError ? err.detail : 'Unexpected error.';
        lifecycleFailed += 1;
      }
    }

    // #20 (2026-06-10): RM creates filed PENDING CRs; refresh the CR
    // cache so phantom rows (usePendingCreates) appear immediately.
    const anyPendingFiled = identityPending + partyPending
      + accountAttachPending + accountDetachPending > 0;
    if (anyPendingFiled) {
      queryClient.invalidateQueries({ queryKey: ['change-requests'] });
    }

    const totalApplied = identityApplied + partyApplied
      + accountAttachApplied + accountDetachApplied + lifecycleApplied + docResult.applied;
    const totalPending = identityPending + partyPending
      + accountAttachPending + accountDetachPending + lifecyclePending + docResult.filed;
    const docFailed = docResult.failedKeys.size;
    const totalFailed = partyFailed + accountFailed + lifecycleFailed + docFailed;

    if (totalFailed === 0) {
      // Full success: clear all pending queues, drop dirty flag,
      // exit edit mode. Aggregate toast distinguishes auto-apply
      // (Compliance/Admin) from CR-pending (RM filings).
      setPendingPartyOps([]);
      setPendingAccountOps([]);
      setPendingPolicyLifecycleOp(null);
      setFailedPartyKeys(new Set());
      setFailedAccountKeys(new Set());
      setFailedLifecycleReason(null);
      setFailedReasons(new Map());
      form.resetDirty(values);
      setEditMode(false);
      if (totalPending > 0 && totalApplied === 0) {
        showAlert({
          color: 'orange',
          title: 'Filed for compliance approval',
          message: totalPending === 1
            ? 'Your change was filed. The live record is unchanged until Compliance approves.'
            : `${totalPending} changes were filed for approval. The live record is unchanged until Compliance approves.`,
        });
      } else if (totalPending > 0) {
        notifications.show({
          message: `Saved · ${totalApplied} auto-applied · ${totalPending} filed for approval`,
          color: 'green',
        });
      } else {
        // #400 (2026-06-09) + Phase 1.5: aggregate per-category breakdown.
        // Identity / Party / Account-attach / Account-detach / Lifecycle.
        const parts: string[] = [];
        if (identityApplied > 0) parts.push(`Identity updated${fieldSuffix(changedLabels)}`);
        // #23: ops are now adds AND detaches — "change(s)" wording.
        if (partyApplied > 0) {
          parts.push(`${partyApplied} ${partyApplied === 1 ? 'Party change applied' : 'Party changes applied'}`);
        }
        if (accountAttachApplied > 0) {
          parts.push(`${accountAttachApplied} ${accountAttachApplied === 1 ? 'Account attached' : 'Accounts attached'}`);
        }
        if (accountDetachApplied > 0) {
          parts.push(`${accountDetachApplied} ${accountDetachApplied === 1 ? 'Account detached' : 'Accounts detached'}`);
        }
        if (lifecycleApplied > 0 && lifecycleLabel) {
          parts.push(lifecycleLabel);
        }
        parts.push(...documentResultParts(docResult));
        notifications.show({
          message: parts.length > 0 ? `Saved · ${parts.join(' · ')}` : 'Saved',
          color: 'green',
        });
      }
    } else {
      // Partial: keep the failed ops in queue with Failed markers so
      // the user can fix and retry via Save. Successful ops have
      // already committed via mutateAsync.
      setPendingPartyOps((prev) =>
        prev.filter((op) => nextFailedPartyKeys.has(op.tempKey)),
      );
      setPendingAccountOps((prev) =>
        prev.filter((op) => nextFailedAccountKeys.has(op.tempKey)),
      );
      setFailedPartyKeys(nextFailedPartyKeys);
      setFailedAccountKeys(nextFailedAccountKeys);
      setFailedReasons(nextFailedReasons);
      setFailedLifecycleReason(nextFailedLifecycleReason);
      // Identity field UPDATE already committed if it was dirty;
      // form.resetDirty so the dirty signal reflects only the
      // remaining failed ops, not the (now-applied) field edits.
      if (form.isDirty()) form.resetDirty(values);
      // Build a precise message pointing the user at the failing tab(s)
      // and the failure reason if there's only one.
      const tabLabels: string[] = [];
      if (partyFailed > 0) tabLabels.push(`${partyFailed} on Parties tab`);
      if (accountFailed > 0) tabLabels.push(`${accountFailed} on Accounts tab`);
      if (lifecycleFailed > 0) tabLabels.push(`lifecycle op on Agreement tab`);
      if (docFailed > 0) tabLabels.push(`${docFailed} on Documents tab`);
      const committedCount = totalApplied + totalPending;
      const committedDesc = committedCount === 0 ? 'no changes committed'
        : committedCount === 1 ? '1 change committed'
        : `${committedCount} changes committed`;
      const allReasons = [...nextFailedReasons.values()];
      if (nextFailedLifecycleReason) allReasons.push(nextFailedLifecycleReason);
      if (docResult.firstError) allReasons.push(docResult.firstError);
      const reasonLine = totalFailed === 1 && allReasons.length === 1
        ? ` Reason: ${allReasons[0]}.`
        : ' Hover the Failed badges to see each reason.';
      showAlert({
        color: 'orange',
        title: 'Partial save',
        message:
          `${committedDesc}, ${totalFailed} failed (${tabLabels.join(', ')}).` +
          reasonLine + ' Failed ops are kept on the page — fix and re-Save.',
      });
    }
  }

  // Beta #11 cascade-delete (2026-05-26).
  const handleCascadeConfirm = (refIds: Record<string, string[]>) => {
    if (!data) return;
    cascadeDelete.mutate(
      { id: data.id, refIds: refIds as Record<string, string[]> },
      {
        onSuccess: () => { setCascadeOpen(false); navigate('/account-insurance'); },
        onError: async (err) => {
          if (err instanceof ApiError && err.code === 'cascade_refs_mismatch') {
            await refs.refetch();
            showAlert({
              color: 'orange',
              title: 'Attachments changed since you opened this dialog',
              message: err.detail + ' The list has been refreshed — please review and confirm again.',
            });
          } else {
            setCascadeOpen(false);
            showAlert({
              color: 'red',
              title: 'Cascade delete failed',
              message: err instanceof ApiError ? err.detail : 'Unexpected error.',
            });
          }
        },
      },
    );
  };

  return (
    <Container size="xl">
      <CascadeDeleteModal
        opened={cascadeOpen}
        onClose={() => setCascadeOpen(false)}
        entityNoun="Insurance policy"
        entityLabel={data ? `Policy ${data.policy_number ?? data.id.slice(0,8)}` : undefined}
        report={refs.data}
        onConfirm={handleCascadeConfirm}
        loading={cascadeDelete.isPending}
      />
      <Stack>
        <StickyDetailHeader
          editing={editMode}
          title={isNew ? 'New insurance policy' : `${data?.insurer ?? '...'} - ${data?.policy_number ?? ''}`}
          backOnClick={() => confirmDiscard(() => {
            // #391 sweep (2026-06-09): clear all dirty state BEFORE
            // navigating so the passive useBlocker (DirtyFormProvider,
            // #258) doesn't re-trigger the discard modal on the
            // navigate. Mirrors the Cancel button's clearing logic.
            form.setValues(initial);
            form.resetDirty(initial);
            setPendingPartyOps([]);
            setPendingAccountOps([]);
            setPendingPolicyLifecycleOp(null);
            setFailedPartyKeys(new Set());
            setFailedAccountKeys(new Set());
            setFailedLifecycleReason(null);
            setFailedReasons(new Map());
            docs.queue.clear();
            setEditMode(false);
            if (returnTo) navigate(returnTo); else navigate(-1);
          })}
          primary={
            // Task #366 (§3g Phase 1 Step A, 2026-06-09): Edit/Save/Cancel
            // buttons are now page-level — visible on every tab, not just
            // Identity. Per §3g Edit mode = a page-level transaction;
            // relationship affordances on Parties / Accounts tabs stage
            // pending ops that this Save button batches with the Identity
            // form UPDATE. Delete/Restore (destructive zone) still gates
            // to Identity since they're whole-entity actions that the
            // user expects to find next to the entity's own fields.
            //
            // Task #245 (2026-06-02): hide Edit on deleted records.
            isDeleted ? null : (
              !editMode ? (
                <Button leftSection={<IconEdit size={16} />} onClick={() => setEditMode(true)}>
                  Edit
                </Button>
              ) : (
                <Group gap="xs">
                  <Button variant="default" leftSection={<IconX size={16} />}
                    onClick={() => {
                      // Task #373 (§3g Phase 1 Step H, 2026-06-09):
                      // Discard clears Identity form AND all pending
                      // Party/Account ops in one gesture — the entire
                      // page-level transaction is abandoned.
                      confirmDiscard(() => {
                        form.setValues(initial);
                        form.resetDirty(initial);
                        setPendingPartyOps([]);
                        setPendingAccountOps([]);
                        setPendingPolicyLifecycleOp(null);
                        setFailedPartyKeys(new Set());
                        setFailedAccountKeys(new Set());
                        setFailedLifecycleReason(null);
                        setFailedReasons(new Map());
                        docs.queue.clear();
                        setEditMode(false);
                      });
                    }}>
                    Cancel
                  </Button>
                  <Button form="account-insurance-form" type="submit"
                    loading={create.isPending || update.isPending || createParty.isPending || attachAccountToPolicy.isPending}
                    disabled={!hasPendingWork && !isNew}>
                    {isNew ? 'Save' : 'Save changes'}
                  </Button>
                </Group>
              )
            )
          }
          destructive={
            // Delete / Restore similarly only make sense alongside the
            // Identity tab — they affect the policy as a whole.
            activeTab !== 'identity' ? null
            : !isNew && data && !isDeleted ? (
              // C-176 (2026-06-16): Delete is a consequential action —
              // Edit-gated per C-172 (greyed-with-reason in Read mode,
              // enabled in Edit). It keeps its own confirm/cascade modal
              // (§3g Pattern B), NOT batched into Save. Matches the
              // AgreementDetailPage reference. Restore (below) stays
              // available in Read mode — the C-172 exception for a
              // deleted row that has no Edit mode.
              <DisabledTooltipButton variant="light" color="red" leftSection={<IconTrash size={16} />}
                disabledReason={editMode ? null : 'Enter Edit mode to delete'}
                enabledTooltip="Deletes this insurance policy record"
                onClick={async () => {
                  const result = await refs.refetch();
                  const report = result.data;
                  if (!report || !report.has_active_references) {
                    confirmDelete('insurance policy', () => del.mutate(data.id, {
                      onSuccess: () => navigate('/account-insurance'),
                      onError: (err) => showAlert({
                        color: 'red', title: 'Delete failed',
                        message: err instanceof ApiError ? err.detail : 'Unexpected error.',
                      }),
                    }));
                  } else {
                    setCascadeOpen(true);
                  }
                }}
                loading={del.isPending || refs.isFetching}>Delete</DisabledTooltipButton>
            ) : !isNew && data && isDeleted ? (
              <Button variant="light" color="green" leftSection={<IconRotate size={16} />}
                onClick={() => restore.mutate(data.id)} loading={restore.isPending}>Restore</Button>
            ) : null
          }
        />

        {/* Phase A3.d (2026-06-09): summary box mirrors the AgreementDetailPage
            pattern. Insurer / Policy # / Fund + a clickable Agreement field
            (or "Orphan" when status=Inactive with no agreement). Status badge
            anchors the right end. */}
        {!isNew && data && (() => {
          const agreementLabel = parentAgreementQ.data
            ? `${parentAgreementQ.data.code_1} — ${parentAgreementQ.data.client_mnemonic}`
            : null;
          const agreementTo = data.agreement_id
            ? `/agreements/${data.agreement_id}`
            : undefined;
          const isOrphan = !data.agreement_id;
          return (
            <Paper withBorder p="md" radius="sm">
              <Group justify="space-between" align="center" wrap="wrap">
                <Group gap="lg" wrap="wrap">
                  <SummaryItem label="Insurer" value={data.insurer} bold />
                  <SummaryItem label="Policy #" value={data.policy_number} mono />
                  <SummaryItem label="Fund" value={data.fund_type} />
                  <SummaryItem
                    label="Agreement"
                    value={isOrphan ? 'Orphan' : agreementLabel}
                    mono={!isOrphan}
                    to={agreementTo}
                  />
                </Group>
                {/* B1/#35 (2026-06-11, old #363): pending-CR state in the
                    header — visible from any tab, next to the status pill. */}
                <Group gap="xs" wrap="nowrap">
                <PendingCRBadge crs={data.pending_change_requests} />
                <StatusExplained entity="policy" status={data.deleted_at ? 'Deleted' : data.status}>
                <Badge size="lg" variant="filled"
                  color={
                    data.deleted_at ? 'red'
                      : data.status === 'Active' ? 'green'
                      : data.status === 'Inactive' ? 'yellow'
                      : 'gray'  // Closed
                  }
                >
                  {data.deleted_at ? 'Deleted' : data.status}
                </Badge>
                </StatusExplained>
                </Group>
              </Group>
            </Paper>
          );
        })()}

        {apiError && <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>{apiError}</Alert>}

        {!isNew && data && (
          <IncompleteBanner entity_type="INSURANCE_POLICY" id={data.id} onOpenTab={setActiveTab} />
        )}

        <Tabs value={activeTab} onChange={setActiveTab} keepMounted>
          {/* H1.6: tab order is the universal Agreement → Party → Account →
              Insurance pattern. Self (Insurance) is omitted; Identity holds
              the policy's own fields. New tabs are disabled until the
              policy is saved (no policy_id to scope cross-refs to yet). */}
          <Tabs.List>
            <Tabs.Tab value="identity"
              rightSection={<TabMissingCount count={missing.tabCounts.identity} />}>Identity</Tabs.Tab>
            <Tabs.Tab value="agreement" disabled={isNew}
              leftSection={<IconClipboardList size={14} />}>
              Agreement
            </Tabs.Tab>
            <Tabs.Tab value="parties" disabled={isNew}
              leftSection={<IconUsers size={14} />}
              rightSection={<TabMissingCount count={missing.tabCounts.parties} />}>
              Parties
            </Tabs.Tab>
            <Tabs.Tab value="accounts" disabled={isNew}
              leftSection={<IconBuildingBank size={14} />}
              rightSection={<TabMissingCount count={missing.tabCounts.accounts} />}>
              Accounts
            </Tabs.Tab>
            <Tabs.Tab value="documents" disabled={isNew}
              leftSection={<IconFiles size={14} />}>
              Documents
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="identity" pt="md">
            {/* #27 (2026-06-11, C-121 sweep): Save is reachable from any
                tab while the validated fields (insurer, policy_number)
                live on Identity — a failure there made Save appear dead.
                Second onSubmit argument names the fields and jumps back
                to Identity. Reference: AgreementDetailPage. */}
            {/* noValidate (#27 round 2): see AccountDetailPage — native
                required-validation intercepts the submit before React;
                Mantine validators own the job instead. */}
            <form id="account-insurance-form" noValidate onSubmit={form.onSubmit(handleSubmit, (errors) => {
              const fields = Object.keys(errors);
              const labels = fields.map((f) => labelizeField(f));
              setActiveTab('identity'); // all validated fields live there
              showAlert({
                color: 'red',
                title: 'Cannot save — fix the highlighted field(s) first',
                message: `${labels.join(', ')}: ${fields.map((f) => String(errors[f])).join('; ')}`,
              });
            })}>
              <Stack pos="relative">
                <LoadingOverlay visible={isLoading} />
                <fieldset disabled={!editMode} style={{ border: 'none', padding: 0, margin: 0 }}>
                  {isNew && presetAccountId && (
                    <Paper withBorder p="md" radius="sm" bg="azul.0" mb="md">
                      <Group justify="space-between" align="center">
                        <div>
                          <Text size="xs" c="dimmed" tt="uppercase" mb={2}>Will attach to account</Text>
                          <AccountRef id={presetAccountId} linkPathPrefix="/accounts" />
                        </div>
                        <Text size="xs" c="dimmed">
                          The account is added to this policy on save. Add more on the Accounts tab.
                        </Text>
                      </Group>
                    </Paper>
                  )}
                  {isNew && !presetAccountId && (
                    <Alert color="blue" variant="light" icon={<IconAlertCircle size={16} />} mb="md">
                      You can save the policy without any accounts and attach them later on the
                      Accounts tab. The Compliance dashboard will flag empty policies.
                    </Alert>
                  )}

                  <Fieldset legend="Policy">
                    <Grid>
                      <Grid.Col span={6}><TextInput label="Insurer" required {...form.getInputProps('insurer')} {...hl('insurer')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Policy number" required {...form.getInputProps('policy_number')} {...hl('policy_number')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Fund type" {...form.getInputProps('fund_type')} {...hl('fund_type')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Fund number" {...form.getInputProps('fund_number')} {...hl('fund_number')} /></Grid.Col>
                    </Grid>
                  </Fieldset>

                  <Fieldset legend="Insurer contact">
                    <Grid>
                      <Grid.Col span={6}><TextInput label="Contact name" {...form.getInputProps('contact_name')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Email" type="email" {...form.getInputProps('contact_email')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Phone" {...form.getInputProps('contact_phone')} /></Grid.Col>
                      <Grid.Col span={6}><TextInput label="Fax" {...form.getInputProps('contact_fax')} /></Grid.Col>
                    </Grid>
                  </Fieldset>

                  {/* #274 Wave A.2 (2026-06-08): on create only, surface
                      the target Agreement (read-only, for confirmation)
                      and the orphan-Account multi-select. Hidden on
                      update — the new Policy's Agreement is immutable
                      once saved, and Indirect Accounts are managed via
                      the Accounts tab. */}
                  {isNew && (
                    <Fieldset legend="Agreement &amp; Indirect Accounts">
                      <Stack>
                        {!derivedAgreementId ? (
                          <Alert color="red" icon={<IconAlertCircle size={16} />}>
                            No target Agreement specified. Open this form
                            from an Agreement detail page (+ Add Insurance
                            Policy) or from a Direct Account detail page.
                            The Policy cannot be saved without one.
                          </Alert>
                        ) : (
                          <>
                            <Group gap="xs">
                              <Text size="sm" c="dimmed">Agreement:</Text>
                              <AgreementRef id={derivedAgreementId}
                                linkPathPrefix="/agreements" />
                            </Group>
                            <MultiSelect
                              label="Pre-attach Indirect Accounts (optional)"
                              description={
                                'Pick orphan Accounts (currently Inactive, unattached)'
                                + ' to become Indirect Accounts on the new Policy.'
                                + ' Backend validates orphan eligibility at submit'
                                + ' time. You can also attach Accounts later via the'
                                + ' Accounts tab.'
                              }
                              placeholder={
                                orphansQ.isLoading
                                  ? 'Loading orphan Accounts…'
                                  : orphanOptions.length === 0
                                    ? 'No orphan Accounts available'
                                    : 'Select Accounts to attach…'
                              }
                              data={orphanOptions}
                              value={orphanPicks}
                              onChange={setOrphanPicks}
                              searchable
                              clearable
                              disabled={orphansQ.isLoading || orphanOptions.length === 0}
                            />
                          </>
                        )}
                      </Stack>
                    </Fieldset>
                  )}
                </fieldset>
              </Stack>
            </form>
          </Tabs.Panel>

          <Tabs.Panel value="agreement" pt="md">
            {!isNew && data && (
              <PolicyAgreementSection
                policy={data}
                editMode={editMode}
                pendingLifecycleOp={pendingPolicyLifecycleOp}
                failedLifecycleReason={failedLifecycleReason}
                onStageLifecycleOp={stagePolicyLifecycleOp}
                onUnstageLifecycleOp={unstagePolicyLifecycleOp}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="parties" pt="md">
            {!isNew && id && data && (
              <PolicyPartiesSection
                policyId={id}
                policyLabel={`${data.insurer} - ${data.policy_number}`}
                editMode={editMode}
                pendingOps={pendingPartyOps}
                failedKeys={failedPartyKeys}
                failedReasons={failedReasons}
                onStageCreate={stagePartyCreate}
                onStageDelete={stagePartyDelete}
                onUnstageOp={unstagePartyOp}
                resumePersonId={resumePartyPersonId}
                onResumeHandled={() => setResumePartyPersonId(null)}
              />
            )}
          </Tabs.Panel>

          <Tabs.Panel value="accounts" pt="md">
            {!isNew && id && data && (
              <PolicyAccountsSection
                policyId={id}
                attachedAccountIds={data.account_ids ?? []}
                policyAgreementId={data.agreement_id ?? null}
                editMode={editMode}
                pendingOps={pendingAccountOps}
                failedKeys={failedAccountKeys}
                failedReasons={failedReasons}
                onStageAttach={stageAccountAttach}
                onStageDetach={stageAccountDetach}
                onUnstageOp={unstageAccountOp}
              />
            )}
          </Tabs.Panel>

          {/* P23 (2026-10-10): files on this policy (e.g. the policy pack). */}
          <Tabs.Panel value="documents" pt="md">
            {!isNew && data && (
              <DocumentsPanel ownerType="insurance_policies" ownerId={data.id}
                editMode={editMode} staging={docs} />
            )}
          </Tabs.Panel>
        </Tabs>
      </Stack>
    </Container>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PolicyPartiesSection — Phase H1.5 + §3g Phase 1 (Task #369, 2026-06-09)
//
// Source-of-truth listing for insurance_policy_parties on this policy.
// Edit mode is page-level; in Edit mode an "+ Add party" button opens
// AddPolicyPartyModal, which stages a CREATE op via onStageCreate.
// Pending rows render below DB rows with an orange "Pending Save" badge.
// The parent's Save Changes batches all pending ops through maker-checker.
// ──────────────────────────────────────────────────────────────────────────
interface PolicyPartiesSectionProps {
  policyId: string;
  policyLabel: string;
  editMode: boolean;
  pendingOps: PendingPolicyPartyOp[];
  failedKeys: ReadonlySet<string>;
  failedReasons: ReadonlyMap<string, string>;
  onStageCreate: (payload: InsurancePolicyPartyInput, personDisplayName: string | null) => void;
  /** #23: stage a per-row detach (commits on Save). */
  onStageDelete: (partyId: UUID) => void;
  onUnstageOp: (tempKey: string) => void;
  /** P18: a newly-created person's id ferried back from the "Create new
   *  person" round-trip — reopen the Add-party modal on Attach with it
   *  pre-selected. Cleared via onResumeHandled once consumed (one-shot). */
  resumePersonId?: string | null;
  onResumeHandled?: () => void;
}

function PolicyPartiesSection({
  policyId, policyLabel, editMode, pendingOps, failedKeys, failedReasons,
  onStageCreate, onStageDelete, onUnstageOp, resumePersonId, onResumeHandled,
}: PolicyPartiesSectionProps) {
  // 200, like the other party tabs (the default 50 silently cut the list, C-374).
  const partiesQ = useInsurancePolicyPartiesList(policyId, undefined, undefined, { limit: 200 });
  const returnTo = encodeURIComponent(`/account-insurance/${policyId}?tab=parties`);
  const addModal = useAddPartyModal(resumePersonId, onResumeHandled);

  const pendingCreates = pendingOps.filter(
    (op): op is Extract<PendingPolicyPartyOp, { kind: 'create' }> => op.kind === 'create',
  );
  // #23: rows with a staged detach get the strikethrough + Pending detach
  // treatment (mirrors Agreement Parties, #327 Phase 1).
  const pendingDeleteIds = new Set(
    pendingOps.filter((op) => op.kind === 'delete').map((op) => op.party_id),
  );

  // #20 (2026-06-10): Pedro's app-wide rule — filed-awaiting-approval
  // CREATEs render as phantom rows (blue treatment), distinct from the
  // orange staged-not-yet-saved rows. See hooks/usePendingCreates.
  const filedCreates = usePendingCreates('insurance_policy_parties', 'policy_id', policyId);

  return (
    <Fieldset legend={<Group gap="xs"><IconUsers size={16} /><span>Parties on this policy</span></Group>}>
      <Stack>
        {editMode && (
          <Group justify="flex-end">
            <Button size="xs" leftSection={<IconPlus size={14} />} variant="light"
              onClick={addModal.open}>
              Add party
            </Button>
          </Group>
        )}
        {partiesQ.isLoading && <Text c="dimmed" size="sm">Loading...</Text>}
        {!partiesQ.isLoading
          && (partiesQ.data?.items.length ?? 0) === 0
          && pendingCreates.length === 0 && filedCreates.length === 0 && (
          <Text c="dimmed" size="sm">
            No parties yet — {editMode
              ? 'click "Add party" to record a Policy Holder, Life Insured, or Beneficiary on this policy.'
              : 'enter Edit mode (button at top of page) to add a Policy Holder, Life Insured, or Beneficiary.'}
          </Text>
        )}
        {((partiesQ.data?.items.length ?? 0) > 0 || pendingCreates.length > 0
          || filedCreates.length > 0) && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={140}>Role</Table.Th>
                <Table.Th>Person</Table.Th>
                <Table.Th w={120}>Designation</Table.Th>
                <Table.Th w={100}>Share %</Table.Th>
                <Table.Th w={140}>Designated</Table.Th>
                <Table.Th w={200}>Status</Table.Th>
                <Table.Th w={80}></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {partiesQ.data?.items.map((p) => {
                const pendingDelete = pendingDeleteIds.has(p.id);
                const failed = failedKeys.has(p.id);
                return (
                <Table.Tr key={p.id}
                  // #5 (2026-06-16): opacity dims the row; line-through on Role
                  // pill + Person cell only, status badges stay legible.
                  style={pendingDelete ? { opacity: 0.6 } : undefined}>
                  <Table.Td>
                    <Anchor component={Link}
                      to={`/insurance-policy-parties/${p.id}?return_to=${returnTo}`}
                      size="sm" style={{ cursor: 'pointer' }}>
                      <Badge variant="light" color={ROLE_COLOURS[p.role]}
                        styles={{ root: { cursor: 'pointer' }, label: pendingDelete ? { textDecoration: 'line-through' } : undefined }}>
                        {PARTY_ROLE_LABELS[p.role] ?? p.role}
                      </Badge>
                    </Anchor>
                  </Table.Td>
                  {/* Task #239 (2026-06-02): server-resolved
                      person_display_name instead of <PersonRef> — drops
                      the per-row /persons/<id> cascade. Falls back to
                      short-UUID when the Person row is missing. */}
                  <Table.Td style={pendingDelete ? { textDecoration: 'line-through' } : undefined}>
                    {p.person_display_name ? (
                      <Anchor component={Link} to={`/persons/${p.person_id}`} size="sm">
                        {p.person_display_name}
                      </Anchor>
                    ) : (
                      <Text size="xs" ff="monospace" c="dimmed">{p.person_id.slice(0, 8)}…</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {p.designation ? <Badge variant="light">{p.designation}</Badge> : <Text c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>{p.percentage_share ?? '—'}</Table.Td>
                  <Table.Td>{p.designation_date ?? '—'}</Table.Td>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <Group gap={4} wrap="wrap">
                        <StatusExplained entity="party" status={p.deleted_at ? 'Deleted' : 'Active'}>
                        {p.deleted_at
                          ? <Badge color="gray" variant="light">Deleted</Badge>
                          : <Badge color="green" variant="light">Active</Badge>}
                        </StatusExplained>
                        {pendingDelete && <Badge color="orange" variant="light" size="xs">Pending detach</Badge>}
                        {failed && <Badge color="red" variant="filled" size="xs">Failed</Badge>}
                        {/* #23: RM-filed changes against this row (e.g. a
                            filed detach) stay visible after Save — Task 4
                            badge, same as Agreement Parties. */}
                        <PendingCRBadge crs={p.pending_change_requests} />
                      </Group>
                      {failed && failedReasons.get(p.id) && (
                        <Text size="xs" c="red">{failedReasons.get(p.id)}</Text>
                      )}
                    </Stack>
                  </Table.Td>
                  {/* #23 (2026-06-10): per-row staged detach — trash
                      stages, undo unstages; commits on Save. Soft-deleted
                      rows hide both affordances. Mirrors Agreement
                      Parties + AccountDetailPage Persons tab. */}
                  <Table.Td>
                    <RowSeverIcon
                      pending={pendingDelete}
                      editMode={editMode}
                      deleted={!!p.deleted_at}
                      onStage={() => onStageDelete(p.id)}
                      onUnstage={() => onUnstageOp(p.id)}
                      stageLabel="Detach this party (on Save)"
                    />
                  </Table.Td>
                </Table.Tr>
                );
              })}
              {/* Pending CREATE rows — staged via the Add Party modal.
                  Render below DB rows with an orange "Pending Save" badge
                  so the user sees the new party will commit on Save Changes. */}
              {pendingCreates.map((op) => (
                <Table.Tr key={op.tempKey} style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                  <Table.Td>
                    <Badge variant="light" color={ROLE_COLOURS[op.payload.role ?? 'BENEFICIARY']}>
                      {PARTY_ROLE_LABELS[op.payload.role ?? 'BENEFICIARY']}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {op.person_display_name ? (
                      <Text size="sm">{op.person_display_name}</Text>
                    ) : (
                      <Text size="xs" ff="monospace" c="dimmed">
                        {(op.payload.person_id ?? '').slice(0, 8)}…
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {op.payload.designation
                      ? <Badge variant="light">{op.payload.designation}</Badge>
                      : <Text c="dimmed">—</Text>}
                  </Table.Td>
                  <Table.Td>{op.payload.percentage_share ?? '—'}</Table.Td>
                  <Table.Td>{op.payload.designation_date ?? '—'}</Table.Td>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <Group gap={4}>
                        <Badge color="orange" variant="light" size="sm">Pending Save</Badge>
                        {failedKeys.has(op.tempKey) && (
                          <Badge color="red" variant="filled" size="sm">Failed</Badge>
                        )}
                      </Group>
                      {failedKeys.has(op.tempKey) && failedReasons.get(op.tempKey) && (
                        <Text size="xs" c="red">
                          {failedReasons.get(op.tempKey)}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  {editMode && (
                    <Table.Td>
                      {/* C-117: undo-arrow icon is THE row undo shape. */}
                      <Tooltip label="Unstage" withArrow>
                        <ActionIcon size="sm" variant="subtle" color="gray"
                          onClick={() => onUnstageOp(op.tempKey)}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
              {/* #20: filed-pending CREATE phantom rows — blue treatment
                  ("filed, awaiting approval"), badge links to the change
                  request. Sourced from pending CREATE CRs whose payload
                  targets this policy. */}
              {filedCreates.map((cr) => {
                const p = cr.payload as {
                  person_id?: string; role?: string; designation?: string;
                  percentage_share?: number; designation_date?: string;
                };
                return (
                  <Table.Tr key={cr.id}
                    style={{ backgroundColor: 'var(--mantine-color-blue-0)' }}>
                    <Table.Td>
                      {p.role ? (
                        <Badge variant="light" color={ROLE_COLOURS[p.role as InsurancePolicyPartyRole] ?? 'gray'}>
                          {PARTY_ROLE_LABELS[p.role] ?? p.role}
                        </Badge>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>
                      <PersonRef id={p.person_id} linkPathPrefix="/persons" />
                    </Table.Td>
                    <Table.Td>
                      {p.designation
                        ? <Badge variant="light">{p.designation}</Badge>
                        : <Text c="dimmed">—</Text>}
                    </Table.Td>
                    <Table.Td>{p.percentage_share ?? '—'}</Table.Td>
                    <Table.Td>{p.designation_date ?? '—'}</Table.Td>
                    <Table.Td>
                      <Tooltip
                        label="Filed for approval — Compliance must approve before the party becomes active. Click to open the change request."
                        withArrow>
                        <Anchor component={Link} to={`/change-requests/${cr.id}`} size="sm">
                          <Badge color="blue" variant="light" size="xs"
                            styles={{ root: { cursor: 'pointer' } }}>
                            Pending approval
                          </Badge>
                        </Anchor>
                      </Tooltip>
                    </Table.Td>
                    {editMode && <Table.Td />}
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <AddPolicyPartyModal
        opened={addModal.opened}
        onClose={addModal.close}
        policyId={policyId}
        policyLabel={policyLabel}
        initialPersonId={addModal.initialPersonId}
        onStage={(payload, personDisplayName) => {
          onStageCreate(payload, personDisplayName);
          addModal.close();
        }}
      />
    </Fieldset>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PolicyAgreementSection — Phase H1.6
//
// Read-only display of the agreement(s) this policy is linked to.
// Derived: policy → linked accounts (via insurance_policy_accounts) → each
// account's agreement_id. By the same-agreement DB invariant, all of a
// policy's accounts share one agreement, so we expect exactly one row.
// If account_ids is empty (a policy in the empty/_has_account-flagged
// state), show a hint that no agreement is yet derivable.
// ──────────────────────────────────────────────────────────────────────────
interface PolicyAgreementSectionProps {
  policy: AccountInsurance;
  /** Phase 1.5 (#375): a singleton lifecycle op currently staged on the
   *  parent page (detach-whole-policy OR terminate-with-reason). Drives
   *  the "staged" banner + disables the Detach/Terminate triggers while
   *  staged so the user can't queue two competing lifecycle changes. */
  pendingLifecycleOp: PendingPolicyLifecycleOp | null;
  failedLifecycleReason: string | null;
  /** C-176 (2026-06-16): lifecycle controls are Edit-gated per C-172
   *  (Read-mode-is-read-only). In Read mode Detach/Terminate render
   *  greyed-with-reason, not clickable — they previously fired in Read
   *  mode and stranded the user (the staged-op banner pointed at a Save
   *  button only visible in Edit mode). */
  editMode: boolean;
  onStageLifecycleOp: (op: PendingPolicyLifecycleOp) => void;
  onUnstageLifecycleOp: () => void;
}

function PolicyAgreementSection({
  policy, editMode, pendingLifecycleOp, failedLifecycleReason,
  onStageLifecycleOp, onUnstageLifecycleOp,
}: PolicyAgreementSectionProps) {
  // #275 (2026-06-08): post-0028 the Policy carries agreement_id directly
  // (no more derive-from-first-account). Active policies have a non-null
  // agreement_id; Inactive/Closed policies are in limbo with agreement_id
  // NULL. Detach is offered for Active policies; re-attach (via the
  // chooser on AgreementDetailPage) is the inverse path.
  const [detachOpen, setDetachOpen] = React.useState(false);
  // gap-2 (2026-06-16): locally-picked Agreement for re-attaching a limbo Policy.
  const [attachTarget, setAttachTarget] = React.useState<UUID | null>(null);
  // #322 (2026-06-08): single modal for all three terminate reasons —
  // reason is captured at the moment the menu item is clicked.
  const [terminateReason, setTerminateReason] = React.useState<UserCloseReason | null>(null);
  const isLimbo = policy.agreement_id === null;
  // Detach + Terminate apply to the same precondition (Active + attached).
  // Closed policies fail this gate (status='Closed' → agreement_id NULL).
  // Phase 1.5: also disabled when a lifecycle op is already staged
  // (singleton; can't queue two competing ones).
  const canMutate = policy.status === 'Active'
    && !policy.deleted_at
    && policy.agreement_id !== null
    && pendingLifecycleOp === null;
  // #322: distinguish "limbo (Inactive, reversible)" from "closed (terminal)"
  // in the no-Agreement branch — Closed policies need their own messaging.
  const isClosed = policy.status === 'Closed';

  return (
    <Fieldset legend={<Group gap="xs"><IconClipboardList size={16} /><span>Agreement</span></Group>}>
      <Stack>
        {isLimbo ? (
          <Paper withBorder p="md" radius="sm">
            <Group gap="xs" mb={4}>
              {isClosed ? (
                <>
                  <Badge color="red" variant="light">Closed</Badge>
                  {policy.close_reason && (
                    <Badge color="red" variant="outline">{CLOSE_REASON_LABELS[policy.close_reason]}</Badge>
                  )}
                  <Text size="sm" fw={500}>Policy is terminated</Text>
                </>
              ) : (
                <>
                  <Badge color="yellow" variant="light">Limbo</Badge>
                  <Text size="sm" fw={500}>Policy is detached</Text>
                </>
              )}
            </Group>
            {isClosed ? (
              <Text size="xs" c="dimmed">
                This Policy reached a terminal Closed state and cannot be re-attached or restored.
              </Text>
            ) : pendingLifecycleOp?.kind === 'attach-to-agreement' ? (
              <Text size="xs" c="dimmed">
                A re-attach to an Agreement is staged — see the banner below. It commits when you click Save Changes.
              </Text>
            ) : (
              // gap-2 (2026-06-16, Phase 4b / C-167): attach a limbo Policy to an
              // Agreement from the Policy side — the mirror of the Agreement-side
              // "+ Add Insurance Policy → Attach". Pattern A (stages on the page
              // transaction, commits on Save); Edit-gated per C-172.
              <Stack gap="xs">
                <Text size="xs" c="dimmed">
                  This Policy is in Inactive limbo with no Agreement. Attach it to an
                  Agreement below — it commits on Save and the Policy becomes Active again.
                </Text>
                <Group gap="xs" align="flex-end" wrap="nowrap">
                  <AgreementPicker
                    label="Agreement"
                    placeholder="Search agreements…"
                    value={attachTarget}
                    onChange={setAttachTarget}
                    disabled={!editMode}
                  />
                  <DisabledTooltipButton
                    size="xs" variant="light"
                    leftSection={<IconClipboardList size={14} />}
                    disabledReason={!editMode
                      ? 'Enter Edit mode to attach'
                      : !attachTarget
                        ? 'Pick an Agreement first'
                        : null}
                    onClick={() => {
                      if (attachTarget) {
                        onStageLifecycleOp({ kind: 'attach-to-agreement', agreement_id: attachTarget });
                        setAttachTarget(null);
                      }
                    }}>
                    Attach to this Agreement (on Save)
                  </DisabledTooltipButton>
                </Group>
              </Stack>
            )}
          </Paper>
        ) : (
          <Paper withBorder p="md" radius="sm" bg="azul.0">
            <Group justify="space-between" align="center">
              <div>
                <Group gap="xs" wrap="nowrap">
                  <Text size="xs" c="dimmed" tt="uppercase" mb={2}>Parent agreement</Text>
                  {/* #26 (2026-06-10): filed-awaiting-approval CRs against
                      this policy (detach/terminate/move) stay visible
                      where the lifecycle triggers live. */}
                  <PendingCRBadge crs={policy.pending_change_requests} />
                </Group>
                <AgreementRef id={policy.agreement_id} linkPathPrefix="/agreements" />
              </div>
              <Group gap="xs">
                <Anchor component={Link} to={`/agreements/${policy.agreement_id}`}
                  size="sm" fw={500}>Open agreement →</Anchor>
                {/* C-176 (2026-06-16): Edit-gated per C-172. canMutate
                    keeps the status/attach/no-pending-op precondition; the
                    editMode gate is layered as greyed-with-reason (§3h.3 /
                    C-110) so these can't fire in Read mode and strand a
                    staged op (Save only renders in Edit mode). */}
                {canMutate && (
                  <DisabledTooltipButton size="xs" color="yellow" variant="light"
                    leftSection={<IconUnlink size={14} />}
                    disabledReason={editMode ? null : 'Enter Edit mode to detach'}
                    onClick={() => setDetachOpen(true)}>
                    Detach
                  </DisabledTooltipButton>
                )}
                {canMutate && (editMode ? (
                  <Menu shadow="md" position="bottom-end">
                    <Menu.Target>
                      <Button size="xs" color="red" variant="light"
                        leftSection={<IconCircleX size={14} />}
                        rightSection={<IconChevronDown size={14} />}>
                        Terminate
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>Terminal — cannot be reversed</Menu.Label>
                      <Menu.Item onClick={() => setTerminateReason('REDEEMED')}>
                        Redeem
                        <Text size="xs" c="dimmed">surrendered for cash value</Text>
                      </Menu.Item>
                      <Menu.Item onClick={() => setTerminateReason('LAPSED')}>
                        Lapse
                        <Text size="xs" c="dimmed">non-payment of premiums</Text>
                      </Menu.Item>
                      <Menu.Item onClick={() => setTerminateReason('MATURED')}>
                        Mature
                        <Text size="xs" c="dimmed">reached end of term, paid out</Text>
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                ) : (
                  <DisabledTooltipButton size="xs" color="red" variant="light"
                    leftSection={<IconCircleX size={14} />}
                    rightSection={<IconChevronDown size={14} />}
                    disabledReason="Enter Edit mode to terminate">
                    Terminate
                  </DisabledTooltipButton>
                ))}
              </Group>
            </Group>
            <Text size="xs" c="dimmed" mt="xs">
              The Policy's Agreement is the single source of truth (#272). All
              Indirect Accounts owned by the Policy effectively belong to this
              Agreement. Detach moves the Policy to Inactive limbo (reversible);
              Terminate moves it to Closed (irreversible). Both cascade Active
              Indirect Accounts to the same state.
            </Text>
          </Paper>
        )}
        {/* Phase 1.5 (#375): staged-state banner. When a lifecycle op is
            staged on the parent's transaction, show a yellow notice with
            an Unstage button so the user can drop it without going
            through Discard (which would also blow away any pending
            party/account ops). The actual commit happens on the page's
            Save Changes. */}
        {pendingLifecycleOp && (
          <Alert color="orange" icon={<IconAlertTriangle size={16} />}
            withCloseButton={false}>
            <Group justify="space-between" align="flex-start">
              <Stack gap={2}>
                <Text size="sm" fw={500}>
                  {pendingLifecycleOp.kind === 'detach-whole-policy'
                    ? 'Policy Detach is staged'
                    : pendingLifecycleOp.kind === 'attach-to-agreement'
                    ? 'Policy Attach is staged'
                    : `Policy ${pendingLifecycleOp.reason === 'REDEEMED' ? 'Redeem'
                       : pendingLifecycleOp.reason === 'LAPSED' ? 'Lapse'
                       : 'Mature'} is staged`}
                </Text>
                <Text size="xs" c="dimmed">
                  Click <em>Save Changes</em> in the page header to commit, or
                  Unstage to drop the change. Detach/Terminate triggers are
                  disabled until this is resolved.
                </Text>
                {failedLifecycleReason && (
                  <Group gap={4} mt={4}>
                    <Badge color="red" variant="filled" size="sm">Failed last Save</Badge>
                    <Text size="xs" c="red">{failedLifecycleReason}</Text>
                  </Group>
                )}
              </Stack>
              <Button size="xs" variant="subtle" color="gray"
                onClick={onUnstageLifecycleOp}>
                Unstage
              </Button>
            </Group>
          </Alert>
        )}
        <DetachPolicyModal
          opened={detachOpen}
          onClose={() => setDetachOpen(false)}
          policy={policy}
          onStage={() => onStageLifecycleOp({ kind: 'detach-whole-policy' })}
        />
        {terminateReason && (
          <TerminatePolicyModal
            opened={terminateReason !== null}
            onClose={() => setTerminateReason(null)}
            policy={policy}
            reason={terminateReason}
            onStage={(reason) => onStageLifecycleOp({ kind: 'terminate', reason })}
          />
        )}
      </Stack>
    </Fieldset>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PolicyAccountStatusBadge — #407 (2026-06-09).
//
// Per-row sub-component used inside PolicyAccountsSection to render an
// Account's actual lifecycle status, not a hardcoded "Active". Pulls the
// Account via useAccount (React Query — deduped + cached, so multiple
// rows pointing at the same Account share one fetch; new rows added
// during a session use whatever cache state is current).
//
// Trade-off: N rows = N hook calls. Acceptable for typical policies
// (1-10 accounts). For pathological cases the right fix is Option B in
// the #407 task description — extend the backend AccountInsurance shape
// to include the child accounts' statuses in one round-trip. That's
// deferred until a real perf concern surfaces.
//
// Mapping: Active → green, Inactive → yellow (limbo), Closed → red
// (terminal), Deleted → gray. Loading → muted "…" placeholder.
// ──────────────────────────────────────────────────────────────────────────
function PolicyAccountStatusBadge({ accountId }: { accountId: UUID }) {
  const { data, isLoading } = useAccount(accountId, false);
  if (isLoading || !data) {
    return <Badge color="gray" variant="light" size="sm">…</Badge>;
  }
  const status = data.status;
  const color = status === 'Active'   ? 'green'
              : status === 'Inactive' ? 'yellow'
              : status === 'Closed'   ? 'red'
              :                          'gray';
  return (
    <StatusExplained entity="account" status={status}>
      <Badge color={color} variant="light">{status}</Badge>
    </StatusExplained>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// PolicyAccountsSection — Phase H1.6 + §3g Phase 1 (Task #370, 2026-06-09)
//
// Source-of-truth listing for insurance_policy_accounts. Single table:
// each row = one alive join. In Edit mode an inline AccountPicker +
// Attach button stages an attach op via onStageAttach (NamedRMSection-
// style — "sparse" relationship-add pattern). Pending rows render below
// DB rows with an orange "Pending Save" badge. The parent's Save Changes
// batches all pending attaches through maker-checker.
//
// Per-row Detach is deferred to §3g Phase 1.5 — remains Pattern B today.
// ──────────────────────────────────────────────────────────────────────────
interface PolicyAccountsSectionProps {
  policyId: string;
  /** Indirect Account IDs currently attached to this policy. Sourced from
   *  the policy detail's `data.account_ids` (post-0028 single source of
   *  truth). Replaced the broken `useInsurancePolicyAccountsList` query
   *  on 2026-06-08 (#376) — the legacy GET /insurance-policy-accounts
   *  endpoint was removed when the join table was dropped in migration
   *  0028 and never replaced. */
  attachedAccountIds: ReadonlyArray<UUID>;
  /** Agreement the policy belongs to. Drives the AccountPicker's same-
   *  agreement filter so the user can't stage a cross-agreement attach
   *  (the DB trigger would reject it anyway). null when the policy is
   *  in limbo (detached). */
  policyAgreementId: UUID | null;
  editMode: boolean;
  pendingOps: PendingPolicyAccountOp[];
  failedKeys: ReadonlySet<string>;
  failedReasons: ReadonlyMap<string, string>;
  onStageAttach: (accountId: UUID) => void;
  /** Phase 1.5 (#375): stage a per-row Account detach (Indirect → orphan)
   *  for a currently-attached account. The committed row stays visible
   *  with an orange "Pending detach" overlay until Save commits. */
  onStageDetach: (linkAccountId: UUID) => void;
  onUnstageOp: (tempKey: string) => void;
}

function PolicyAccountsSection({
  policyId, attachedAccountIds, policyAgreementId: _policyAgreementId,
  editMode, pendingOps, failedKeys, failedReasons, onStageAttach, onStageDetach, onUnstageOp,
}: PolicyAccountsSectionProps) {
  const [pickerValue, setPickerValue] = React.useState<string>('');
  // Task #105 preserved (2026-05-04): a non-empty picker selection without
  // a click on "Attach" still counts as unsaved work — the page-level
  // dirty guard then catches a route-level navigate-away. Pattern A makes
  // the staging explicit (Attach = stage), but the picker can still hold
  // an un-staged selection that the user expects to retain.
  useDirtyFormGuard(!!pickerValue);

  // #376 (2026-06-08): the new POST /account-insurance/{id}/accounts/attach
  // endpoint only accepts ORPHAN Accounts (agreement_id NULL AND
  // opened_by_policy_id NULL AND status='Inactive'). The previous
  // <AccountPicker agreementId={...}> would filter to accounts on a
  // specific agreement and EXCLUDE orphans entirely, so users could
  // never pick a valid attach target. Replaced with an orphans-only
  // Select sourced from useAccounts({orphans_only: true}) — matches the
  // create-flow's orphan multi-select chrome (lines ~456-478) for
  // consistency.
  const orphansQ = useAccounts({ orphans_only: true, limit: 200 });
  const orphanOptions = (orphansQ.data?.items ?? [])
    .filter((a) => !pendingOps.some((op) => op.kind === 'attach' && op.account_id === a.id))
    .map((a) => ({
      value: a.id,
      label: `${a.bank_name}${a.internal_num ? ' #' + a.internal_num : ''}${a.iban ? ' (' + a.iban + ')' : ''}`,
    }));

  const pendingAttaches = pendingOps.filter(
    (op): op is Extract<PendingPolicyAccountOp, { kind: 'attach' }> => op.kind === 'attach',
  );
  // Phase 1.5 (#375): map keyed by link_account_id so the row renderer
  // can look up "is this committed row marked for detach?" in O(1).
  // The map value is the pending op itself so the row can read tempKey
  // (for Unstage) and failure state.
  const pendingDetachByAccountId = new Map<UUID, Extract<PendingPolicyAccountOp, { kind: 'detach' }>>();
  for (const op of pendingOps) {
    if (op.kind === 'detach') pendingDetachByAccountId.set(op.link_account_id, op);
  }

  function handleStage() {
    if (!pickerValue) return;
    onStageAttach(pickerValue);
    setPickerValue('');
  }

  const showTable = attachedAccountIds.length > 0 || pendingAttaches.length > 0;

  return (
    <Stack>
      {/* Phase A2.c polish (2026-06-09): affordances block lifted OUT of
          the "Accounts on this policy" Fieldset so the Fieldset becomes
          a pure read view of current state. Two sibling affordances —
          Create New (navigates) and Attach Orphan (stages) — separated
          by a muted "or". Both gated by editMode (§3g). Refinement
          discussed with Pedro 2026-06-09 during A2.d. */}
      {editMode && (
        <Stack gap="xs">
          <Group justify="flex-start">
            <Button
              component={Link}
              to={`/accounts/new?preset_policy_id=${policyId}&return_to=${encodeURIComponent(`/account-insurance/${policyId}?tab=accounts`)}`}
              leftSection={<IconPlus size={14} />}
              variant="light"
              size="sm"
            >
              Create new Indirect Account
            </Button>
          </Group>
          <Group justify="flex-start">
            <Text size="xs" c="dimmed">or</Text>
          </Group>
          <Group align="flex-end">
            <Select
              label="Attach existing orphan account"
              description="Only orphan Accounts (no Agreement, no Policy, Inactive) are eligible. Attaching promotes the account to Active Indirect on this Policy."
              placeholder={orphansQ.isLoading
                ? 'Loading orphan accounts…'
                : orphanOptions.length === 0
                  ? 'No orphan accounts available'
                  : 'Pick an orphan account…'}
              data={orphanOptions}
              value={pickerValue || null}
              onChange={(v) => setPickerValue(v ?? '')}
              searchable
              clearable
              disabled={orphansQ.isLoading || orphanOptions.length === 0}
              style={{ flex: 1 }}
            />
            <Button leftSection={<IconPlus size={14} />}
              onClick={handleStage}
              disabled={!pickerValue}>
              Attach
            </Button>
          </Group>
        </Stack>
      )}

      <Fieldset legend={<Group gap="xs"><IconBuildingBank size={16} /><span>Accounts on this policy</span></Group>}>
        <Stack>

        {!showTable && (
          <Text c="dimmed" size="sm">
            No accounts linked yet. {editMode
              ? 'Use the picker above to stage attachments — they’ll commit when you click Save Changes.'
              : 'Enter Edit mode (button at top of page) to attach an account.'}
            {' '}The Compliance dashboard’s Incomplete Records will flag this policy
            under the _has_account rule.
          </Text>
        )}
        {showTable && (
          <Table withTableBorder striped>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Account</Table.Th>
                <Table.Th w={200}>Status</Table.Th>
                {/* #401 (2026-06-09): stable Actions column in Edit mode
                    so the column structure doesn't shift when a pending
                    row appears with an Unstage button. Phase 1.5 (#375)
                    populates this cell with per-row Detach. */}
                {editMode && <Table.Th w={140}>Actions</Table.Th>}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {attachedAccountIds.map((accId) => {
                // Phase 1.5 (#375): a committed row may have a staged
                // detach. If so, render it with orange tint + a
                // "Pending detach" badge alongside the Active badge;
                // the Actions cell shows Unstage instead of Detach.
                const stagedDetach = pendingDetachByAccountId.get(accId);
                const isFailedDetach = stagedDetach && failedKeys.has(stagedDetach.tempKey);
                const failureMsg = stagedDetach ? failedReasons.get(stagedDetach.tempKey) : undefined;
                return (
                  <Table.Tr key={accId} style={stagedDetach
                    ? { backgroundColor: 'var(--mantine-color-orange-0)' }
                    : undefined}>
                    <Table.Td><AccountRef id={accId} linkPathPrefix="/accounts" /></Table.Td>
                    <Table.Td>
                      <Stack gap={2} align="flex-start">
                        <Group gap={4}>
                          {/* #407 (2026-06-09): badge reflects the actual
                              Account.status (fetched per-row via useAccount)
                              instead of a hardcoded "Active". Closed the
                              cascade-stale-display bug exposed by #375
                              Test 8: a whole-Policy Detach correctly
                              cascades child Accounts to Inactive in the DB
                              but the rendered rows were stuck on green
                              Active until manual refresh. */}
                          <PolicyAccountStatusBadge accountId={accId} />
                          {stagedDetach && (
                            <Badge color="orange" variant="light" size="sm">Pending detach</Badge>
                          )}
                          {isFailedDetach && (
                            <Badge color="red" variant="filled" size="sm">Failed</Badge>
                          )}
                        </Group>
                        {isFailedDetach && failureMsg && (
                          <Text size="xs" c="red">{failureMsg}</Text>
                        )}
                      </Stack>
                    </Table.Td>
                    {editMode && (
                      <Table.Td>
                        {/* C-117 (2026-06-10): row-scoped staged actions
                            are icons + tooltip; undo-arrow is THE row
                            undo shape. */}
                        {stagedDetach ? (
                          <Tooltip label="Undo staged detach" withArrow>
                            <ActionIcon size="sm" variant="subtle" color="gray"
                              onClick={() => onUnstageOp(stagedDetach.tempKey)}>
                              <IconArrowBackUp size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : (
                          <Tooltip label="Detach this account (on Save)" withArrow>
                            <ActionIcon size="sm" variant="subtle" color="orange"
                              onClick={() => onStageDetach(accId)}>
                              <IconUnlink size={14} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Table.Td>
                    )}
                  </Table.Tr>
                );
              })}
              {/* Pending ATTACH rows — render orange, with a Failed
                  badge if the most recent Save attempt rejected this
                  op (typically a 409 duplicate or cross-agreement
                  trigger). User can Unstage to drop or Save to retry. */}
              {pendingAttaches.map((op) => (
                <Table.Tr key={op.tempKey} style={{ backgroundColor: 'var(--mantine-color-orange-0)' }}>
                  <Table.Td><AccountRef id={op.account_id} linkPathPrefix="/accounts" /></Table.Td>
                  <Table.Td>
                    <Stack gap={2} align="flex-start">
                      <Group gap={4}>
                        <Badge color="orange" variant="light" size="sm">Pending Save</Badge>
                        {failedKeys.has(op.tempKey) && (
                          <Badge color="red" variant="filled" size="sm">Failed</Badge>
                        )}
                      </Group>
                      {failedKeys.has(op.tempKey) && failedReasons.get(op.tempKey) && (
                        <Text size="xs" c="red">
                          {failedReasons.get(op.tempKey)}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  {editMode && (
                    <Table.Td>
                      {/* C-117: undo-arrow icon is THE row undo shape. */}
                      <Tooltip label="Unstage" withArrow>
                        <ActionIcon size="sm" variant="subtle" color="gray"
                          onClick={() => onUnstageOp(op.tempKey)}>
                          <IconArrowBackUp size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </Table.Td>
                  )}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
        </Stack>
      </Fieldset>
    </Stack>
  );
}
